"use client";

import React, { useEffect, useRef, useState } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";

import type { ScanResult } from "@/app/api/atsumary/scan/route";
import { auth } from "@/lib/firebase/client";

// ─── Constants ────────────────────────────────────────────────────────────────

/** 業界標準単価（採用工数コスト算出用） */
const HOURLY_RATE_CEO = 5_000;       // 経営者・役員
const HOURLY_RATE_RECRUITER = 3_000; // 採用担当

const CHECKLIST_ITEMS = [
  { key: "ceoVideo",    label: "社長・代表者の動画がある",         bonus: true  },
  { key: "staffPhoto",  label: "スタッフの実際の写真がある",         bonus: true  },
  { key: "daySchedule", label: "1日のタイムスケジュールがある",      bonus: true  },
  { key: "mission",     label: "経営理念・ミッションが書かれている",  bonus: false },
  { key: "careerPath",  label: "キャリアパスが図解・説明されている",  bonus: true  },
] as const;

type ChecklistKey = typeof CHECKLIST_ITEMS[number]["key"];

// ─── Industry ─────────────────────────────────────────────────────────────────

const INDUSTRIES = [
  { value: "", label: "業界を選択（任意）" },
  { value: "介護", label: "🏥 介護・福祉" },
  { value: "IT", label: "💻 IT・テック" },
  { value: "建設", label: "🏗 建設・施工" },
  { value: "営業", label: "📈 営業・セールス" },
  { value: "医療", label: "🏨 医療・クリニック" },
  { value: "飲食", label: "🍽 飲食・サービス" },
  { value: "製造", label: "🏭 製造・メーカー" },
];

// ─── Care industry 5-question diagnosis ──────────────────────────────────────

const KAIGO_QUESTIONS = [
  {
    key: "q1_education",
    question: "新人に「見て覚えろ」が当たり前の教育環境がありますか？",
    hint: "「ある」場合、LP Problem セクションの強力な訴求ネタになります",
    yesLabel: "はい（課題あり）",
    noLabel: "いいえ（整備済み・強み）",
    yesIsBad: true,
  },
  {
    key: "q2_variability",
    question: "先輩によって教え方がバラバラな状態ですか？",
    hint: "「統一研修」がある施設は圧倒的な強みです",
    yesLabel: "はい（バラバラ）",
    noLabel: "いいえ（統一されている）",
    yesIsBad: true,
  },
  {
    key: "q3_return_rate",
    question: "育休・産休を取得したスタッフが全員職場に復帰していますか？",
    hint: "「復帰率100%」はLP最強の証拠（Proof）になります",
    yesLabel: "はい（全員復帰）",
    noLabel: "いいえ / 該当者なし",
    yesIsBad: false,
  },
  {
    key: "q4_2026_revision",
    question: "2026年介護報酬改定に合わせた採用訴求の見直しを実施しましたか？",
    hint: "「報酬改定対応済み」は求職者の安心感につながります",
    yesLabel: "はい（対応済み）",
    noLabel: "いいえ（未対応）",
    yesIsBad: false,
  },
  {
    key: "q5_buzzwords",
    question: "現在の求人票に「やりがい」「アットホーム」という言葉が入っていますか？",
    hint: "この言葉は求職者の信頼を下げます。具体的な本音表現が効果的です",
    yesLabel: "入っている（要改善）",
    noLabel: "入っていない（良好）",
    yesIsBad: true,
  },
] as const;

type KaigoQuestionKey = typeof KAIGO_QUESTIONS[number]["key"];

// BDFパターンラベル
const BDF_PATTERN_LABELS = {
  A: { name: "負の解消型", description: "業界の理不尽さを代弁し、自社の「まともさ」を強調" },
  B: { name: "プライド再定義型", description: "介護を「ケア接客」と定義し直し、プロとしての誇りを刺激" },
  C: { name: "事実・制度証明型", description: "復帰率100%・時間単位有給などの数字で「安心感」を担保" },
} as const;

const LOADING_MESSAGES = [
  "競合他社と求人票を比較中...",
  "経営理念の熱量を測定中...",
  "BDF（本音・悩み・欲求）を解析中...",
  "バイブスレベルを診断中...",
  "採用機会損失額を算定中...",
  "採用マーケティングAIが診断レポートを作成中...",
];

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatCurrency(n: number): string {
  return `¥${Math.round(n).toLocaleString("ja-JP")}`;
}

function getGrade(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "A", color: "#22c55e" };
  if (score >= 60) return { label: "B", color: "#f59e0b" };
  if (score >= 40) return { label: "C", color: "#f97316" };
  return { label: "D", color: "#ef4444" };
}

function scoreColor(v: number): string {
  if (v >= 70) return "#22c55e";
  if (v >= 40) return "#f59e0b";
  return "#ef4444";
}

/** 広告費モード */
type AdMode = "high" | "medium" | "low";
function getAdMode(adSpend: number): AdMode {
  if (adSpend >= 300_000) return "high";
  if (adSpend <= 50_000)  return "low";
  return "medium";
}

// ─── Loss Breakdown ───────────────────────────────────────────────────────────

type LossBreakdown = {
  adLoss: number;              // 広告費ロス（年間）
  timeLoss: number;            // 採用工数コスト（年間）
  opportunityLoss: number;     // 欠員による機会損失（年間）
  total: number;               // 合計
  lossRate: number;            // ロス率 0-100
  monthlyHours: number;        // 月間採用工数合計（時間）
  ceoMonthlyHours: number;     // うち経営者・役員分
  recruiterMonthlyHours: number; // うち採用担当分
  vacancyMonths: number;       // 欠員期間（ヶ月）
};

/**
 * スコアと広告費から損失内訳を計算する。
 * 広告費の水準によって各項目の重みを動的に変化させる。
 */
function computeBreakdown(score: number, adSpend: number): LossBreakdown {
  const lossRate = (100 - score) / 100; // 0.0〜1.0

  // 広告費ロス（年間）
  // 高広告費モードほど係数大、低広告費モードは小
  const adMultiplier = adSpend >= 300_000 ? 1.2 : adSpend <= 50_000 ? 0.8 : 1.0;
  const adLoss = Math.round(adSpend * lossRate * adMultiplier * 12);

  // 採用工数コスト（経営者: 時給5,000円 / 採用担当: 時給3,000円）
  // スコアが低いほど工数増加（20h〜60h/月）
  // 低広告費モードは経営者工数が主因なので係数増
  const baseHours = 20;
  const extraHours = Math.round((100 - score) * 0.4); // 0〜40h
  const modeHourMultiplier = adSpend <= 50_000 ? 1.5 : 1.0;
  const monthlyHours = Math.round((baseHours + extraHours) * modeHourMultiplier);
  // 月間工数を役割別に分割（経営者40% / 採用担当60%）
  const ceoMonthlyHours = Math.round(monthlyHours * 0.4);
  const recruiterMonthlyHours = monthlyHours - ceoMonthlyHours;
  const timeLoss = (HOURLY_RATE_CEO * ceoMonthlyHours + HOURLY_RATE_RECRUITER * recruiterMonthlyHours) * 12;

  // 欠員による機会損失（月間粗利50万円/人）
  // スコアが低いほど欠員期間が長い（1〜4ヶ月）
  const vacancyMonths = Math.max(1, Math.round(1 + (100 - score) / 33));
  const opportunityLoss = 500_000 * vacancyMonths;

  return {
    adLoss,
    timeLoss,
    opportunityLoss,
    total: adLoss + timeLoss + opportunityLoss,
    lossRate: Math.round(lossRate * 100),
    monthlyHours,
    ceoMonthlyHours,
    recruiterMonthlyHours,
    vacancyMonths,
  };
}

// ─── Circle Gauge ─────────────────────────────────────────────────────────────

function CircleGauge({ score }: { score: number }) {
  const [display, setDisplay] = useState(0);
  const r = 76;
  const circ = 2 * Math.PI * r;
  const { color } = getGrade(score);

  useEffect(() => {
    const t = setTimeout(() => setDisplay(score), 80);
    return () => clearTimeout(t);
  }, [score]);

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={184} height={184} viewBox="0 0 184 184">
        <circle cx={92} cy={92} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={14} />
        <circle
          cx={92} cy={92} r={r}
          fill="none" stroke={color} strokeWidth={14}
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - display / 100)}
          strokeLinecap="round"
          transform="rotate(-90 92 92)"
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[52px] font-black text-white leading-none tabular-nums">{display}</span>
        <span className="text-[11px] font-black text-white/30 mt-0.5">点 / 100</span>
      </div>
    </div>
  );
}

// ─── Radar Chart ──────────────────────────────────────────────────────────────

function RadarChart({
  scores,
}: {
  scores: { vision: number; growth: number; evidence: number; safety: number; funnel: number };
}) {
  const cx = 130, cy = 130, r = 85;
  const labels = ["ビジョン", "成長", "証拠", "安心", "導線"];
  const values = [scores.vision, scores.growth, scores.evidence, scores.safety, scores.funnel];
  const n = 5;

  const pt = (i: number, radius: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  };

  const polyStr = (radius: number) =>
    Array.from({ length: n }, (_, i) => `${pt(i, radius).x},${pt(i, radius).y}`).join(" ");

  const scorePolyStr = values
    .map((v, i) => { const p = pt(i, r * (v / 100)); return `${p.x},${p.y}`; })
    .join(" ");

  return (
    <svg width={260} height={260} viewBox="0 0 260 260">
      {[0.25, 0.5, 0.75, 1].map((lvl, li) => (
        <polygon key={li} points={polyStr(r * lvl)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
      ))}
      {Array.from({ length: n }, (_, i) => {
        const p = pt(i, r);
        return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />;
      })}
      <polygon points={scorePolyStr} fill="rgba(239,68,68,0.2)" stroke="#ef4444" strokeWidth={2} />
      {values.map((v, i) => {
        const p = pt(i, r * (v / 100));
        return <circle key={i} cx={p.x} cy={p.y} r={4} fill="#ef4444" />;
      })}
      {labels.map((label, i) => {
        const p = pt(i, r + 26);
        const anchor = Math.abs(p.x - cx) < 5 ? "middle" : p.x < cx ? "end" : "start";
        return (
          <text key={i} x={p.x} y={p.y} textAnchor={anchor} dominantBaseline="middle"
            fill="rgba(255,255,255,0.55)" fontSize={11} fontWeight={700}>
            {label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Score Bar ────────────────────────────────────────────────────────────────

function ScoreBar({ label, score }: { label: string; score: number }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(score), 150);
    return () => clearTimeout(t);
  }, [score]);

  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <span className="text-[11px] font-bold text-white/60">{label}</span>
        <span className="text-[11px] font-black text-white">{score}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8">
        <div
          className="h-1.5 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${width}%`, background: scoreColor(score) }}
        />
      </div>
    </div>
  );
}

// ─── Diagnosis Matrix ─────────────────────────────────────────────────────────

function getUrgencyLevel(score: number): "緊急" | "注意" | "良好" {
  if (score >= 70) return "良好";
  if (score >= 40) return "注意";
  return "緊急";
}

function derivePattern(result: ScanResult): "A" | "B" | "C" {
  if (result.recommendedPattern) return result.recommendedPattern;
  const s = result.scores;
  const groups = [
    { p: "A" as const, v: (s.vision + s.growth) / 2 },
    { p: "B" as const, v: (s.evidence + s.safety) / 2 },
    { p: "C" as const, v: s.funnel },
  ];
  return groups.reduce((min, g) => (g.v < min.v ? g : min)).p;
}

const MATRIX_CELL_LABELS: Record<string, Record<string, string>> = {
  緊急: { A: "訴求ゼロ・即再設計", B: "信頼崩壊・緊急対応", C: "導線壊滅・要全面見直し" },
  注意: { A: "方向性ブレ・要整理",  B: "証拠薄・補強必要",   C: "離脱多発・要改善" },
  良好: { A: "方向性OK・深化を",   B: "信頼OK・次ステップへ", C: "導線OK・最適化を" },
};

const URGENCY_STYLES: Record<string, { border: string; bg: string; text: string; label: string }> = {
  緊急: { border: "rgba(239,68,68,0.55)", bg: "rgba(239,68,68,0.13)", text: "#f87171", label: "🔴 緊急" },
  注意: { border: "rgba(245,158,11,0.55)", bg: "rgba(245,158,11,0.13)", text: "#fbbf24", label: "🟡 注意" },
  良好: { border: "rgba(34,197,94,0.55)", bg: "rgba(34,197,94,0.13)", text: "#4ade80", label: "🟢 良好" },
};

function DiagnosisMatrix({ result }: { result: ScanResult }) {
  const urgency = getUrgencyLevel(result.scores.total);
  const pattern = derivePattern(result);
  const urgencies = ["緊急", "注意", "良好"] as const;
  const patterns = ["A", "B", "C"] as const;

  return (
    <div className="rounded-3xl border p-8" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.025)" }}>
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs font-black tracking-widest text-white/40">ポジション マトリクス</div>
        <div className="flex items-center gap-2 text-sm font-black" style={{ color: URGENCY_STYLES[urgency].text }}>
          <span>{URGENCY_STYLES[urgency].label}</span>
          <span className="text-white/20">×</span>
          <span>パターン {pattern}</span>
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "72px 1fr 1fr 1fr", gap: "8px" }}>
        {/* Header */}
        <div />
        {(["A", "B", "C"] as const).map((p) => {
          const desc = p === "A" ? "ビジョン・成長" : p === "B" ? "証拠・安心" : "導線・訴求";
          return (
            <div key={p} className="text-center pb-2">
              <div className="text-sm font-black text-white/60">パターン {p}</div>
              <div className="text-[11px] font-bold text-white/35 mt-1">{desc}</div>
            </div>
          );
        })}

        {/* Rows */}
        {urgencies.map((u) => {
          const ust = URGENCY_STYLES[u];
          return (
            <React.Fragment key={u}>
              <div className="flex flex-col items-end justify-center pr-3 gap-1">
                <span className="text-sm font-black" style={{ color: ust.text }}>{u}</span>
                <span className="text-[10px] font-bold text-white/30">
                  {u === "緊急" ? "〜39点" : u === "注意" ? "40〜69点" : "70点〜"}
                </span>
              </div>
              {patterns.map((p) => {
                const isActive = urgency === u && pattern === p;
                return (
                  <div
                    key={p}
                    className="rounded-xl border p-4 text-center transition-all duration-300"
                    style={{
                      borderColor: isActive ? ust.border : "rgba(255,255,255,0.06)",
                      background: isActive ? ust.bg : "rgba(255,255,255,0.02)",
                      opacity: isActive ? 1 : 0.35,
                      boxShadow: isActive ? `0 0 20px ${ust.bg}` : "none",
                    }}
                  >
                    {isActive && (
                      <div className="text-[11px] font-black mb-2" style={{ color: ust.text }}>◆ 現在地</div>
                    )}
                    <div className="text-xs font-bold leading-snug" style={{ color: isActive ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)" }}>
                      {MATRIX_CELL_LABELS[u][p]}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      <p className="mt-5 text-[10px] font-bold text-white/25">
        緊急度: 総合スコアから自動判定 · パターン: 最弱スコア軸から自動判定（介護業界はAI判定）
      </p>
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepDots({ current }: { current: 1 | 2 }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-8">
      {([1, 2, 3] as const).map((s) => (
        <React.Fragment key={s}>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
            current >= s ? "bg-rose-600 text-white" : "bg-white/8 text-white/25"
          }`}>
            {s}
          </div>
          {s < 3 && (
            <div className={`h-px w-8 transition-colors duration-300 ${current > s ? "bg-rose-600" : "bg-white/10"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Loss Breakdown Card ──────────────────────────────────────────────────────

function LossBreakdownCard({
  breakdown,
  adMode,
  adSpend,
  score,
}: {
  breakdown: LossBreakdown;
  adMode: AdMode;
  adSpend: number;
  score: number;
}) {
  const items = [
    {
      icon: "💸",
      label: "広告費ロス",
      value: breakdown.adLoss,
      detail: `${formatCurrency(adSpend)}/月 × 損失率${breakdown.lossRate}% × 12ヶ月`,
      emphasis: adMode === "high",
    },
    {
      icon: "⏱",
      label: "採用工数コスト",
      value: breakdown.timeLoss,
      detail: `（時給${HOURLY_RATE_CEO.toLocaleString()}円 × 月${breakdown.ceoMonthlyHours}h）＋（時給${HOURLY_RATE_RECRUITER.toLocaleString()}円 × 月${breakdown.recruiterMonthlyHours}h）× 12ヶ月`,
      emphasis: adMode === "low",
    },
    {
      icon: "📉",
      label: "欠員機会損失",
      value: breakdown.opportunityLoss,
      detail: `月間粗利50万円 × 欠員${breakdown.vacancyMonths}ヶ月分`,
      emphasis: adMode === "low",
    },
  ];

  // モード別メッセージ
  const modeNote =
    adMode === "high"
      ? { color: "#f59e0b", icon: "🪣", text: `月${formatCurrency(adSpend)}の広告費が「穴の空いたバケツ」に流れています。集客しても、このページでは離脱させ続けます。` }
      : adMode === "low"
      ? { color: "#22d3ee", icon: "⏰", text: `広告費が少ない分、経営者の採用工数（月${breakdown.monthlyHours}h）と欠員期間（${breakdown.vacancyMonths}ヶ月）が損失の主因です。` }
      : null;

  const agencyNote = adMode === "high"
    ? { show: true, text: "紹介会社経由の場合、採用1名の取りこぼしは年収の30%＝約" }
    : null;

  return (
    <div className="rounded-3xl border overflow-hidden"
      style={{ borderColor: "rgba(239,68,68,0.35)", background: "linear-gradient(160deg, rgba(100,0,0,0.25), rgba(50,0,0,0.4))" }}
    >
      {/* 合計損失額 */}
      <div className="px-7 pt-7 pb-5 text-center border-b" style={{ borderColor: "rgba(239,68,68,0.2)" }}>
        <div className="text-[10px] font-black tracking-widest text-rose-500/70 mb-2">
          推定年間採用機会損失額
        </div>
        <div className="text-[44px] sm:text-[56px] font-black text-rose-500 leading-none tabular-nums">
          {formatCurrency(breakdown.total)}
        </div>
        <div className="mt-2 text-[11px] font-bold text-white/25">
          広告費ロス + 工数コスト + 欠員機会損失の合計
        </div>
      </div>

      {/* 3カラム内訳 */}
      <div className="grid grid-cols-3 divide-x divide-red-500/15">
        {items.map((item) => (
          <div
            key={item.label}
            className="px-4 py-5 text-center relative"
            style={{
              background: item.emphasis ? "rgba(239,68,68,0.07)" : "transparent",
            }}
          >
            {item.emphasis && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 text-[9px] font-black text-rose-400 tracking-wider">
                主因
              </div>
            )}
            <div className="text-xl mb-1.5 mt-1">{item.icon}</div>
            <div className="text-[10px] font-black text-white/40 mb-1">{item.label}</div>
            <div className="text-base font-black text-white tabular-nums leading-tight">
              {formatCurrency(item.value)}
            </div>
            <div className="mt-2 text-[9px] font-bold text-white/25 leading-relaxed">
              {item.detail}
            </div>
          </div>
        ))}
      </div>

      {/* モード別ヒント */}
      {modeNote && (
        <div
          className="mx-5 mb-4 mt-1 rounded-2xl border px-4 py-3 flex items-start gap-3"
          style={{ borderColor: `${modeNote.color}30`, background: `${modeNote.color}0d` }}
        >
          <span className="flex-shrink-0 text-base">{modeNote.icon}</span>
          <p className="text-[11px] font-bold leading-relaxed" style={{ color: `${modeNote.color}cc` }}>
            {modeNote.text}
          </p>
        </div>
      )}

      {/* 紹介会社ノート */}
      {agencyNote?.show && (
        <div
          className="mx-5 mb-5 rounded-2xl border px-4 py-3 flex items-start gap-3"
          style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.07)" }}
        >
          <span className="flex-shrink-0 text-base">🤝</span>
          <p className="text-[11px] font-bold text-amber-400/80 leading-relaxed">
            {agencyNote.text}
            <strong className="text-amber-300"> 150万円の損失</strong>
            に相当します。このページ品質では、紹介会社へのコストが最も高くつく可能性があります。
          </p>
        </div>
      )}

      {/* 損失率バー */}
      <div className="px-6 pb-3">
        <div className="flex justify-between text-[10px] font-bold text-white/30 mb-1.5">
          <span>損失率</span>
          <span className="text-rose-400 font-black">{breakdown.lossRate}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/8">
          <LossBar pct={breakdown.lossRate} score={score} />
        </div>
      </div>

      {/* 算出根拠注釈 */}
      <div className="px-6 pb-5">
        <p className="text-[9px] font-bold text-white/20 leading-relaxed">
          ※日本の管理職・経営者の平均的な時給換算に基づき算出
        </p>
      </div>
    </div>
  );
}

function LossBar({ pct, score }: { pct: number; score: number }) {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 200); return () => clearTimeout(t); }, [pct]);
  const color = score >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <div className="h-1.5 rounded-full transition-all duration-1000 ease-out" style={{ width: `${w}%`, background: color }} />
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ScanPage() {
  const [step, setStep] = useState<1 | 2 | "loading" | 3>(1);
  const [jobText, setJobText] = useState("");
  const [adSpend, setAdSpend] = useState(100_000);
  const [adUnit, setAdUnit] = useState<"monthly" | "annual">("monthly");
  const [industry, setIndustry] = useState("");
  const [checklist, setChecklist] = useState<Record<ChecklistKey, boolean>>({
    ceoVideo: false, staffPhoto: false, daySchedule: false, mission: false, careerPath: false,
  });
  const [careAnswers, setCareAnswers] = useState<Record<KaigoQuestionKey, "yes" | "no" | "">>({
    q1_education: "", q2_variability: "", q3_return_rate: "", q4_2026_revision: "", q5_buzzwords: "",
  });
  const [result, setResult] = useState<ScanResult | null>(null);
  const [breakdown, setBreakdown] = useState<LossBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingIdx, setLoadingIdx] = useState(0);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const strategyPrintRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auth + save state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<Array<{ id: string; text: string; totalScore: number; weakestPoint: string; createdAt: string }>>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // メール登録フォーム state（介護モード専用）
  const [leadEmail, setLeadEmail] = useState("");
  const [leadFacility, setLeadFacility] = useState("");
  const [leadStatus, setLeadStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [leadError, setLeadError] = useState<string | null>(null);

  const isKaigo = industry === "介護";

  // 月換算値（APIと損失計算は常に月額ベースで処理）
  const monthlyAdSpend = adUnit === "annual" ? Math.round(adSpend / 12) : adSpend;

  /** 単位を切り替えるときに表示値を自動変換 */
  const handleUnitChange = (unit: "monthly" | "annual") => {
    if (unit === adUnit) return;
    if (unit === "annual") {
      // 月額 → 年額換算（12倍してstep=120,000に丸め）
      const annual = Math.round(adSpend * 12 / 120_000) * 120_000;
      setAdSpend(Math.min(12_000_000, Math.max(120_000, annual)));
    } else {
      // 年額 → 月額換算（1/12してstep=10,000に丸め）
      const monthly = Math.round(adSpend / 12 / 10_000) * 10_000;
      setAdSpend(Math.min(1_000_000, Math.max(10_000, monthly)));
    }
    setAdUnit(unit);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setCurrentUser(u));
    return unsub;
  }, []);

  const adMode = getAdMode(monthlyAdSpend);

  // Loading message rotation
  useEffect(() => {
    if (step === "loading") {
      intervalRef.current = setInterval(() => setLoadingIdx((i) => (i + 1) % LOADING_MESSAGES.length), 1800);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [step]);

  const goStep2 = () => {
    if (jobText.trim().length < 30) {
      setError("求人テキストを30文字以上入力してください");
      return;
    }
    setError(null);
    setStep(2);
  };

  const handlePrintStrategy = () => {
    if (!result?.strategyLetter) return;
    const pattern = result.recommendedPattern ?? "A";
    const patternLabel = BDF_PATTERN_LABELS[pattern];
    const grade = getGrade(result.scores.total).label;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!DOCTYPE html>
<html lang="ja"><head><meta charset="UTF-8"><title>介護業界向け採用LP戦略レター</title>
<style>
  body { font-family: 'Hiragino Kaku Gothic Pro', 'BIZ UDPGothic', sans-serif; max-width: 740px; margin: 40px auto; padding: 0 24px; color: #1a1a1a; line-height: 1.9; font-size: 14px; }
  h1 { font-size: 20px; font-weight: 900; border-bottom: 3px solid #1e40af; padding-bottom: 10px; margin-bottom: 24px; }
  h2 { font-size: 15px; font-weight: 700; margin-top: 28px; margin-bottom: 8px; color: #1e40af; }
  .score-row { display: flex; gap: 24px; margin: 16px 0; }
  .score-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 18px; text-align: center; }
  .score-card .num { font-size: 32px; font-weight: 900; color: #dc2626; }
  .score-card .lbl { font-size: 11px; color: #6b7280; }
  .pattern-badge { display: inline-block; background: #1e40af; color: white; padding: 5px 16px; border-radius: 999px; font-size: 13px; font-weight: 700; margin-bottom: 8px; }
  .body-text { white-space: pre-wrap; background: #f8fafc; border-left: 4px solid #1e40af; padding: 16px 20px; border-radius: 0 8px 8px 0; margin-top: 8px; }
  .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; }
  @media print { body { margin: 20px auto; } }
</style></head><body>
<h1>介護業界向け 採用LP戦略レター</h1>
<div class="score-row">
  <div class="score-card"><div class="num">${result.scores.total}</div><div class="lbl">総合スコア</div></div>
  <div class="score-card"><div class="num" style="color:#1e40af">${grade}</div><div class="lbl">グレード</div></div>
  <div class="score-card"><div class="num" style="font-size:18px">${result.weakestPoint}</div><div class="lbl">最弱点</div></div>
</div>
<h2>推奨訴求パターン</h2>
<span class="pattern-badge">パターン ${pattern}: ${patternLabel.name}</span>
<p>${patternLabel.description}</p>
<h2>戦略レター本文</h2>
<div class="body-text">${(result.strategyLetter || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
<div class="footer">Atsumary 採用LP戦略AI · ${new Date().toLocaleDateString("ja-JP")} 発行</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  const diagnose = async () => {
    setStep("loading");
    setError(null);
    setLoadingIdx(0);
    try {
      const r = await fetch("/api/atsumary/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: jobText,
          checklist: isKaigo ? {} : checklist,
          adSpend: monthlyAdSpend,
          industry: industry || undefined,
          careAnswers: isKaigo ? careAnswers : undefined,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(String(j.error || "診断に失敗しました"));

      const scanResult = j.result as ScanResult;
      setResult(scanResult);
      const bd = computeBreakdown(scanResult.scores.total, monthlyAdSpend);
      setBreakdown(bd);
      // UTAGEへの送信用にsessionStorageへ保存
      if (typeof window !== "undefined") {
        sessionStorage.setItem("utage_annualLoss", String(bd.total));
        sessionStorage.setItem("utage_score", String(scanResult.scores.total));
      }
      setRewriteOpen(false);
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : "診断に失敗しました");
      setStep(2);
    }
  };

  const reset = () => {
    setStep(1); setJobText(""); setResult(null); setBreakdown(null); setError(null); setRewriteOpen(false);
    setSaveStatus("idle"); setIndustry(""); setAdSpend(100_000); setAdUnit("monthly");
    setCareAnswers({ q1_education: "", q2_variability: "", q3_return_rate: "", q4_2026_revision: "", q5_buzzwords: "" });
    setLeadEmail(""); setLeadFacility(""); setLeadStatus("idle"); setLeadError(null);
  };

  /** リード登録 → UTAGE送信 → サンクスページへ遷移 */
  const handleLeadSubmit = async () => {
    if (!result || !breakdown) return;
    const email = leadEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadError("有効なメールアドレスを入力してください");
      return;
    }
    setLeadStatus("submitting");
    setLeadError(null);
    try {
      const qs = new URLSearchParams(window.location.search);
      const rid = qs.get("rid") || "";

      const r = await fetch("/api/atsumary/scan/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          facilityName:       leadFacility.trim(),
          industry,
          totalScore:         result.scores.total,
          lossTotal:          breakdown.total,
          adSpend:            monthlyAdSpend,
          recommendedPattern: result.recommendedPattern,
          weakestPoint:       result.weakestPoint,
          diagnosis:          result.diagnosis,
          strategyLetter:     result.strategyLetter,
          feedback:           result.feedback,
          rewrite:            result.rewrite,
          rid,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(String(j.error || "登録に失敗しました"));

      setLeadStatus("done");
      // サンクスページへ遷移
      const params = new URLSearchParams({
        score:     String(result.scores.total),
        loss:      String(breakdown.total),
        pattern:   result.recommendedPattern || "",
        reportUrl: j.reportUrl || "",
      });
      window.location.href = `/atsumary/scan/thanks?${params.toString()}`;
    } catch (e) {
      setLeadError(e instanceof Error ? e.message : "登録に失敗しました");
      setLeadStatus("idle");
    }
  };

  const handleSave = async () => {
    if (!currentUser || !result || saveStatus !== "idle") return;
    setSaveStatus("saving");
    try {
      const token = await currentUser.getIdToken();
      const r = await fetch("/api/atsumary/scan/save", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: jobText, adSpend, result }),
      });
      if (!r.ok) throw new Error("保存失敗");
      setSaveStatus("saved");
      setHistory([]); // clear cache so next open re-fetches
    } catch {
      setSaveStatus("error");
    }
  };

  const fetchHistory = async () => {
    if (!currentUser) return;
    setHistoryLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const r = await fetch("/api/atsumary/scan/history", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("履歴取得失敗");
      const j = await r.json() as { items: Array<{ id: string; text: string; totalScore: number; weakestPoint: string; createdAt: string }> };
      setHistory(j.items || []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!historyOpen && history.length === 0) void fetchHistory();
    setHistoryOpen((v) => !v);
  };

  // ── スライダーのモード表示ラベル ──
  const adModeLabel =
    adMode === "high" ? { text: "高額広告費モード：広告浪費の視点で診断", color: "#f59e0b" }
    : adMode === "low" ? { text: "低広告費モード：時間・機会損失を重点診断", color: "#22d3ee" }
    : { text: "標準モード：3軸バランス診断", color: "#a78bfa" };

  return (
    <div className="min-h-screen text-white" style={{ background: "#080810" }}>

      {/* ── Header ── */}
      <div className="border-b px-4 py-4" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-rose-500 text-lg">⚡</span>
            <div>
              <div className="text-[11px] font-black tracking-widest text-white/30">ATSUMARY</div>
              <div className="text-sm font-black text-white/90 leading-none">採用機会損失スコア診断 AI</div>
            </div>
          </div>
          <a
            href="/peopleos/atsumary/builder"
            className="px-3 py-1.5 rounded-xl bg-indigo-600/80 text-[11px] font-black text-white hover:bg-indigo-500 transition-colors"
          >
            LP生成ツールへ →
          </a>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10 pb-20">

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div className="space-y-7">
            {/* 過去の診断履歴 (ログイン済みのみ) */}
            {currentUser && (
              <div>
                <button
                  type="button"
                  onClick={toggleHistory}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-left text-[11px] font-black text-white/40 hover:bg-white/8 transition-colors flex items-center justify-between"
                >
                  <span>🕒 過去の診断履歴</span>
                  <span>{historyOpen ? "▲" : "▼"}</span>
                </button>
                {historyOpen && (
                  <div className="mt-2 rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                    {historyLoading ? (
                      <div className="py-4 text-center text-[11px] font-bold text-white/30">読み込み中...</div>
                    ) : history.length === 0 ? (
                      <div className="py-4 text-center text-[11px] font-bold text-white/30">保存済みの診断はありません</div>
                    ) : (
                      <div className="divide-y divide-white/5">
                        {history.map((h) => (
                          <div key={h.id} className="px-4 py-3 flex items-center justify-between">
                            <div>
                              <div className="text-[11px] font-black text-white/70 line-clamp-1">{h.text || "（テキスト未保存）"}</div>
                              <div className="mt-0.5 text-[10px] font-bold text-white/30">
                                最弱: {h.weakestPoint} · {h.createdAt ? new Date(h.createdAt).toLocaleDateString("ja-JP") : ""}
                              </div>
                            </div>
                            <div className="text-lg font-black" style={{ color: h.totalScore >= 70 ? "#4ade80" : h.totalScore >= 50 ? "#facc15" : "#f87171" }}>
                              {h.totalScore}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <StepDots current={1} />

            <div className="text-center space-y-3">
              <div className="inline-block rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-1.5 text-[10px] font-black tracking-widest text-rose-400">
                RECRUITMENT LOSS SCANNER
              </div>
              <h1 className="text-[28px] sm:text-[36px] font-black text-white leading-tight">
                あなたの求人票、<br />
                <span className="text-rose-500">何百万円</span>を損していますか？
              </h1>
              <p className="text-sm font-bold text-white/40 max-w-md mx-auto leading-relaxed">
                求人票をAIが辛口診断。採用機会損失額を広告費・時間・機会の3軸で可視化します。
              </p>
            </div>

            {/* Textarea */}
            <div>
              <label className="flex justify-between mb-2">
                <span className="text-xs font-black text-white/50">
                  求人票・募集要項テキスト <span className="text-rose-500">*</span>
                </span>
                <span className="text-[10px] font-bold text-white/20">{jobText.length}文字</span>
              </label>
              <textarea
                value={jobText}
                onChange={(e) => { setJobText(e.target.value); setError(null); }}
                rows={10}
                placeholder={"求人票のテキストをここにペーストしてください...\n\n例：「営業マネージャー募集！月給30万〜50万円。完全週休2日。未経験歓迎...」"}
                className="w-full rounded-2xl border px-5 py-4 text-sm font-bold text-white/85 placeholder-white/15 outline-none resize-y transition-colors"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  borderColor: error ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.09)",
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(239,68,68,0.4)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = error ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.09)"; }}
              />
            </div>

            {/* Industry selector */}
            <div className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.025)" }}>
              <div className="flex justify-between items-center">
                <span className="text-xs font-black text-white/50">業界（任意・選ぶとAIが業界特化診断を実施）</span>
                {isKaigo && <span className="text-[10px] font-black text-indigo-400 border border-indigo-500/30 rounded-lg px-2 py-0.5">介護特化モード ON</span>}
              </div>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full rounded-xl border px-3 py-2.5 text-sm font-bold text-white outline-none cursor-pointer"
                style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                {INDUSTRIES.map((i) => (
                  <option key={i.value} value={i.value} style={{ background: "#1a1a2e" }}>{i.label}</option>
                ))}
              </select>
              {isKaigo && (
                <p className="text-[11px] font-bold text-indigo-400/70 leading-relaxed">
                  ✦ STEP 2 が介護特化5問診断に切り替わります。「やりがい」などの抽象ワードも自動減点されます。
                </p>
              )}
            </div>

            {/* Ad spend slider */}
            <div className="rounded-2xl border p-5 space-y-3" style={{ borderColor: "rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.025)" }}>
              <div className="flex justify-between items-center">
                <span className="text-xs font-black text-white/50">採用広告費</span>
                {/* 月額 / 年額 トグル */}
                <div className="flex rounded-lg border border-white/10 overflow-hidden text-[10px] font-black">
                  {(["monthly", "annual"] as const).map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      onClick={() => handleUnitChange(unit)}
                      className="px-3 py-1.5 transition-colors"
                      style={{
                        background: adUnit === unit ? "#dc2626" : "transparent",
                        color: adUnit === unit ? "#fff" : "rgba(255,255,255,0.35)",
                      }}
                    >
                      {unit === "monthly" ? "月額" : "年額"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-lg font-black text-rose-400">{formatCurrency(adSpend)}</span>
                {adUnit === "annual" && (
                  <span className="text-[10px] font-bold text-white/30">
                    月換算: {formatCurrency(monthlyAdSpend)}/月
                  </span>
                )}
              </div>
              <input
                type="range"
                min={adUnit === "annual" ? 120_000 : 10_000}
                max={adUnit === "annual" ? 12_000_000 : 1_000_000}
                step={adUnit === "annual" ? 120_000 : 10_000}
                value={adSpend}
                onChange={(e) => setAdSpend(Number(e.target.value))}
                className="w-full accent-rose-500"
              />
              <div className="flex justify-between text-[10px] font-bold text-white/20">
                <span>{adUnit === "annual" ? "¥120,000" : "¥10,000"}</span>
                <span>{adUnit === "annual" ? "¥12,000,000" : "¥1,000,000"}</span>
              </div>
              {/* 動的モードラベル */}
              <div
                className="rounded-xl border px-3 py-2 text-[11px] font-black text-center transition-colors duration-300"
                style={{ borderColor: `${adModeLabel.color}33`, background: `${adModeLabel.color}0d`, color: adModeLabel.color }}
              >
                {adModeLabel.text}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-bold text-rose-400">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={goStep2}
              className="w-full rounded-2xl py-4 text-sm font-black text-white transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #dc2626, #9f1239)", boxShadow: "0 8px 32px rgba(220,38,38,0.3)" }}
            >
              次へ：要素チェックリスト →
            </button>
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div className="space-y-7">
            <StepDots current={2} />

            {isKaigo ? (
              /* ── 介護特化5問診断 ── */
              <>
                <div className="text-center space-y-2">
                  <div className="text-[11px] font-black tracking-widest text-indigo-400">STEP 2 / 3 · 介護特化診断</div>
                  <h2 className="text-xl font-black text-white">施設の現状を5問で診断します</h2>
                  <p className="text-xs font-bold text-white/35">回答に基づいて「最適な訴求パターン（A/B/C）」を自動判定します</p>
                </div>

                <div className="space-y-3">
                  {KAIGO_QUESTIONS.map((q, idx) => {
                    const answer = careAnswers[q.key];
                    return (
                      <div
                        key={q.key}
                        className="rounded-2xl border p-5"
                        style={{
                          borderColor: answer ? "rgba(99,102,241,0.4)" : "rgba(255,255,255,0.08)",
                          background: answer ? "rgba(99,102,241,0.06)" : "rgba(255,255,255,0.025)",
                        }}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-6 h-6 rounded-full bg-indigo-600/30 border border-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-[10px] font-black text-indigo-300">{idx + 1}</span>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white leading-snug">{q.question}</p>
                            <p className="text-[10px] font-bold text-white/35 mt-1">{q.hint}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-9">
                          {(["yes", "no"] as const).map((v) => {
                            const label = v === "yes" ? q.yesLabel : q.noLabel;
                            const isSelected = answer === v;
                            const isBad = v === "yes" ? q.yesIsBad : !q.yesIsBad;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setCareAnswers((p) => ({ ...p, [q.key]: v }))}
                                className="flex-1 rounded-xl border py-2 text-[11px] font-black transition-colors"
                                style={{
                                  borderColor: isSelected ? (isBad ? "rgba(239,68,68,0.6)" : "rgba(34,197,94,0.6)") : "rgba(255,255,255,0.1)",
                                  background: isSelected ? (isBad ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)") : "rgba(255,255,255,0.04)",
                                  color: isSelected ? (isBad ? "#fca5a5" : "#86efac") : "rgba(255,255,255,0.5)",
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              /* ── 通常チェックリスト ── */
              <>
                <div className="text-center space-y-2">
                  <div className="text-[11px] font-black tracking-widest text-rose-500">STEP 2 / 3</div>
                  <h2 className="text-xl font-black text-white">ページの要素を確認してください</h2>
                  <p className="text-xs font-bold text-white/35">AIが自動判定できない視覚コンテンツを確認します</p>
                </div>

                <div className="space-y-2.5">
                  {CHECKLIST_ITEMS.map((item) => {
                    const checked = checklist[item.key];
                    return (
                      <label
                        key={item.key}
                        className="flex items-center gap-4 rounded-2xl border px-5 py-4 cursor-pointer transition-all duration-200"
                        style={{
                          borderColor: checked ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.08)",
                          background: checked ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.025)",
                        }}
                      >
                        <div
                          className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                          style={{ borderColor: checked ? "#ef4444" : "rgba(255,255,255,0.2)", background: checked ? "#ef4444" : "transparent" }}
                        >
                          {checked && (
                            <svg viewBox="0 0 10 8" className="w-3 h-3" fill="none">
                              <path d="M1 4L4 7L9 1" stroke="white" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setChecklist((p) => ({ ...p, [item.key]: e.target.checked }))}
                          className="sr-only"
                        />
                        <span className={`text-sm font-bold flex-1 ${checked ? "text-white" : "text-white/55"}`}>
                          {item.label}
                        </span>
                        {checked && (
                          <span className="text-[10px] font-black text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2 py-0.5 flex-shrink-0">
                            +加点
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {error && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-bold text-rose-400">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-2xl border py-4 px-6 text-sm font-black text-white/40 hover:text-white/70 transition-colors"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              >
                ← 戻る
              </button>
              <button
                type="button"
                onClick={() => void diagnose()}
                className="flex-1 rounded-2xl py-4 text-sm font-black text-white transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ background: "linear-gradient(135deg, #dc2626, #9f1239)", boxShadow: "0 8px 32px rgba(220,38,38,0.3)" }}
              >
                🔍 AI診断を開始する
              </button>
            </div>
          </div>
        )}

        {/* ── LOADING ── */}
        {step === "loading" && (
          <div className="flex flex-col items-center justify-center py-24 space-y-10">
            <div className="relative w-24 h-24 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border border-rose-600/20 animate-ping" />
              <div className="absolute inset-2 rounded-full border border-rose-600/30 animate-ping" style={{ animationDelay: "300ms" }} />
              <div
                className="absolute inset-0 rounded-full border-2 border-rose-600"
                style={{ borderTopColor: "transparent", animation: "spin 0.9s linear infinite" }}
              />
              <span className="text-3xl relative z-10">⚡</span>
            </div>
            <div className="text-center space-y-2">
              <div className="text-sm font-black text-white/90 min-h-[24px]">
                {LOADING_MESSAGES[loadingIdx]}
              </div>
              <div className="flex items-center gap-1.5 justify-center">
                {LOADING_MESSAGES.map((_, i) => (
                  <div
                    key={i}
                    className="h-1 rounded-full transition-all duration-500"
                    style={{
                      width: i === loadingIdx ? "24px" : "6px",
                      background: i === loadingIdx ? "#ef4444" : "rgba(255,255,255,0.15)",
                    }}
                  />
                ))}
              </div>
              <div className="text-[11px] font-bold text-white/25">採用マーケティングAIが分析しています</div>
            </div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {step === 3 && result && breakdown && (
          <div className="space-y-5">
            <div className="text-center space-y-1 mb-6">
              <div className="text-[10px] font-black tracking-widest text-rose-500">DIAGNOSIS COMPLETE</div>
              <h2 className="text-xl font-black text-white">診断結果</h2>
            </div>

            {/* Score + Grade */}
            <div className="rounded-3xl border p-6" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <CircleGauge score={result.scores.total} />
                <div className="flex-1 space-y-4 text-center sm:text-left">
                  <div>
                    <div className="text-[10px] font-black tracking-widest text-white/30 mb-1">総合グレード</div>
                    <div className="text-6xl font-black leading-none" style={{ color: getGrade(result.scores.total).color }}>
                      {getGrade(result.scores.total).label}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black tracking-widest text-white/30 mb-1.5">最弱点</div>
                    <div className="inline-block rounded-2xl border px-4 py-2 text-sm font-black text-rose-400"
                      style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)" }}>
                      ⚠ {result.weakestPoint}
                    </div>
                  </div>
                  <p className="text-sm font-bold text-white/55 leading-relaxed">{result.diagnosis}</p>
                </div>
              </div>
            </div>

            {/* ── ポジション マトリクス ── */}
            <DiagnosisMatrix result={result} />

            {/* ── 損失内訳カード（3軸）── */}
            <LossBreakdownCard
              breakdown={breakdown}
              adMode={adMode}
              adSpend={monthlyAdSpend}
              score={result.scores.total}
            />

            {/* ── 5軸レーダー + スコアバー ── */}
            <div className="rounded-3xl border p-6" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.025)" }}>
              <div className="text-[10px] font-black tracking-widest text-white/30 mb-5">5軸スコア分析</div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-shrink-0">
                  <RadarChart scores={{
                    vision: result.scores.vision, growth: result.scores.growth,
                    evidence: result.scores.evidence, safety: result.scores.safety, funnel: result.scores.funnel,
                  }} />
                </div>
                <div className="flex-1 space-y-3.5 w-full">
                  {[
                    { label: "ビジョン", score: result.scores.vision   },
                    { label: "成長",     score: result.scores.growth   },
                    { label: "証拠",     score: result.scores.evidence },
                    { label: "安心",     score: result.scores.safety   },
                    { label: "導線",     score: result.scores.funnel   },
                  ].map(({ label, score }) => (
                    <ScoreBar key={label} label={label} score={score} />
                  ))}
                </div>
              </div>
            </div>

            {/* ── AI Feedback ── */}
            <div className="rounded-3xl border p-6" style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(60,40,0,0.4)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 border"
                  style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.12)" }}>
                  🎯
                </div>
                <div>
                  <div className="text-xs font-black text-amber-400">伝説の採用マーケター</div>
                  <div className="text-[10px] font-bold text-white/25">AI辛口診断コメント</div>
                </div>
              </div>
              <p className="text-sm font-bold text-white/80 leading-relaxed whitespace-pre-line">
                {result.feedback}
              </p>
            </div>

            {/* ── Rewrite Preview ── */}
            <div className="rounded-3xl border p-6" style={{ borderColor: "rgba(99,102,241,0.35)", background: "rgba(20,15,60,0.5)" }}>
              <div className="text-[10px] font-black tracking-widest text-indigo-400 mb-1">ATSUMARY REWRITE PREVIEW</div>
              <div className="text-sm font-black text-white mb-4">⚡ Atsumary仕様でリライトするとこうなります</div>
              <div className="relative">
                <div
                  className="text-sm font-bold text-white/75 leading-relaxed whitespace-pre-line overflow-hidden transition-all duration-500"
                  style={{ maxHeight: rewriteOpen ? "9999px" : "72px" }}
                >
                  {result.rewrite}
                </div>
                {!rewriteOpen && (
                  <div
                    className="absolute bottom-0 inset-x-0 h-14 flex items-end justify-center pb-1"
                    style={{ background: "linear-gradient(to bottom, transparent, rgba(20,15,60,0.97))" }}
                  >
                    <button type="button" onClick={() => setRewriteOpen(true)}
                      className="text-[11px] font-black text-indigo-400 hover:text-indigo-300 transition-colors">
                      ▼ 続きを見る（Atsumaryで全文生成）
                    </button>
                  </div>
                )}
              </div>
              {rewriteOpen && (
                <button type="button" onClick={() => setRewriteOpen(false)}
                  className="mt-3 text-[10px] font-black text-white/20 hover:text-white/40 transition-colors">
                  ▲ 折りたたむ
                </button>
              )}
            </div>

            {/* ── 介護業界向け戦略レター ── */}
            {isKaigo && result.recommendedPattern && result.strategyLetter && (() => {
              const pat = result.recommendedPattern;
              const patLabel = BDF_PATTERN_LABELS[pat];
              return (
                <div
                  className="rounded-3xl border p-6 space-y-4"
                  style={{ borderColor: "rgba(99,102,241,0.35)", background: "linear-gradient(160deg, rgba(30,64,175,0.15), rgba(20,10,60,0.4))" }}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] font-black tracking-widest text-indigo-400 mb-1">KAIGO STRATEGY LETTER</div>
                      <div className="text-sm font-black text-white">介護業界向け 採用LP 戦略レター</div>
                    </div>
                    <button
                      type="button"
                      onClick={handlePrintStrategy}
                      className="flex items-center gap-1.5 rounded-xl border border-indigo-500/40 bg-indigo-600/15 px-3 py-1.5 text-[11px] font-black text-indigo-300 hover:bg-indigo-600/25 transition-colors flex-shrink-0"
                    >
                      📄 PDFで保存
                    </button>
                  </div>

                  {/* 推奨パターンバッジ */}
                  <div
                    className="rounded-2xl border p-4 flex items-center gap-4"
                    style={{ borderColor: "rgba(99,102,241,0.3)", background: "rgba(99,102,241,0.08)" }}
                  >
                    <div className="w-12 h-12 rounded-xl border-2 border-indigo-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-black text-indigo-300">{pat}</span>
                    </div>
                    <div>
                      <div className="text-xs font-black text-indigo-300">推奨訴求パターン</div>
                      <div className="text-sm font-black text-white mt-0.5">{patLabel.name}</div>
                      <div className="text-[11px] font-bold text-indigo-400/70 mt-0.5">{patLabel.description}</div>
                    </div>
                  </div>

                  {/* 戦略レター本文 */}
                  <div
                    className="rounded-2xl border px-5 py-4 text-sm font-bold text-white/80 leading-relaxed whitespace-pre-line"
                    ref={strategyPrintRef}
                    style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
                  >
                    {result.strategyLetter}
                  </div>

                  <p className="text-[10px] font-bold text-white/20">
                    ✦ このレターは診断スコアと5問の回答をもとにAIが生成しました。PDFとして保存・社内共有が可能です。
                  </p>
                </div>
              );
            })()}

            {/* ── 介護モード: メールでレポート受け取り ── */}
            {isKaigo && leadStatus !== "done" && (
              <div
                className="rounded-3xl border p-6 space-y-4"
                style={{ borderColor: "rgba(34,197,94,0.35)", background: "linear-gradient(160deg, rgba(0,60,20,0.3), rgba(0,30,10,0.5))" }}
              >
                <div>
                  <div className="text-[10px] font-black tracking-widest text-green-400 mb-1">REPORT DELIVERY</div>
                  <div className="text-sm font-black text-white">📩 戦略レターをメールで受け取る</div>
                  <p className="text-[11px] font-bold text-white/40 mt-1 leading-relaxed">
                    メールアドレスを入力すると、診断レポートと戦略レターをお送りします。
                  </p>
                </div>

                <div className="space-y-2.5">
                  <input
                    type="email"
                    placeholder="メールアドレス（必須）"
                    value={leadEmail}
                    onChange={(e) => { setLeadEmail(e.target.value); setLeadError(null); }}
                    className="w-full rounded-xl border px-4 py-3 text-sm font-bold text-white placeholder-white/20 outline-none transition-colors"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      borderColor: leadError ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.1)",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(34,197,94,0.4)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = leadError ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.1)"; }}
                  />
                  <input
                    type="text"
                    placeholder="施設名・会社名（任意）"
                    value={leadFacility}
                    onChange={(e) => setLeadFacility(e.target.value)}
                    className="w-full rounded-xl border px-4 py-3 text-sm font-bold text-white placeholder-white/20 outline-none transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(34,197,94,0.4)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
                  />
                </div>

                {leadError && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] font-bold text-rose-400">
                    {leadError}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => void handleLeadSubmit()}
                  disabled={leadStatus === "submitting"}
                  className="w-full rounded-2xl py-3.5 text-sm font-black text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #16a34a, #15803d)", boxShadow: "0 8px 24px rgba(22,163,74,0.3)" }}
                >
                  {leadStatus === "submitting" ? "送信中..." : "📩 メールで受け取る（無料）"}
                </button>

                <p className="text-[10px] font-bold text-white/20 text-center">
                  ✦ スパムは一切送りません。いつでも配信停止できます。
                </p>
              </div>
            )}

            {/* ── CTA ── */}
            <div
              className="rounded-3xl border p-8 text-center space-y-5"
              style={{ borderColor: "rgba(99,102,241,0.4)", background: "linear-gradient(135deg, rgba(49,46,129,0.4), rgba(76,29,149,0.3))" }}
            >
              <div className="text-3xl">🚀</div>
              <div className="text-xl sm:text-2xl font-black text-white leading-tight">
                この欠陥を<span className="text-indigo-400">すべて埋めた</span>採用LPを<br />
                <span className="text-indigo-400">1分で生成</span>しませんか？
              </div>
              <p className="text-sm font-bold text-white/45 leading-relaxed max-w-md mx-auto">
                Atsumaryは話すだけで採用LPを自動生成します。<br />
                年間<span className="text-indigo-300 font-black">{formatCurrency(breakdown.total)}</span>の
                損失を止めるLPへ変換します。
              </p>
              <a
                href="/peopleos/atsumary/builder"
                className="block w-full rounded-2xl py-4 text-sm font-black text-white hover:opacity-90 transition-opacity"
                style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)", boxShadow: "0 8px 32px rgba(79,70,229,0.4)" }}
              >
                無料でLP生成を試す →
              </a>
              {/* Save button */}
              {currentUser ? (
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saveStatus !== "idle"}
                  className="text-[11px] font-bold transition-colors disabled:cursor-default"
                  style={{ color: saveStatus === "saved" ? "#4ade80" : saveStatus === "error" ? "#f87171" : "rgba(255,255,255,0.3)" }}
                >
                  {saveStatus === "saving" ? "💾 保存中..." : saveStatus === "saved" ? "✓ 診断を保存しました" : saveStatus === "error" ? "✕ 保存失敗" : "💾 この診断を保存する"}
                </button>
              ) : (
                <a href="/login" className="text-[11px] font-bold text-white/20 hover:text-white/40 transition-colors">
                  ログインして診断を保存
                </a>
              )}
              <button type="button" onClick={reset}
                className="text-[11px] font-bold text-white/20 hover:text-white/40 transition-colors">
                別の求人票を診断する
              </button>
            </div>

          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
